import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowLeft, Play } from "lucide-react";
import Marquee from "@/components/Marquee";
import { FadeUp, MaskedLine, PageShell, Eyebrow, EASE } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";

function Hero() {
  const c = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <motion.img
          initial={{ scale: 1.18, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease: EASE, delay: 2.1 }}
          src={c.hero.image}
          alt="Bungalow built turnkey by Sai Leela Decor, illuminated at dusk"
          className="w-full h-[115%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/25 to-obsidian" />
      </motion.div>

      <p className="absolute right-6 top-1/2 -translate-y-1/2 hidden xl:block text-[10px] uppercase tracking-[0.5em] text-bone/40 [writing-mode:vertical-rl]">
        {c.hero.sideText}
      </p>

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-14 pt-40">
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 2.0, ease: EASE }}
          className="origin-left flex items-center gap-4 mb-8"
        >
          <span className="h-px w-14 bg-bronze" />
          <Eyebrow>{c.hero.eyebrow}</Eyebrow>
        </motion.div>

        <h1 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-bone max-w-4xl">
          <MaskedLine delay={2.05}>{c.hero.line1}</MaskedLine>
          <MaskedLine delay={2.18} className="italic text-bronze">{c.hero.line2}</MaskedLine>
          <MaskedLine delay={2.31}>{c.hero.line3}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.6, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg font-light leading-relaxed text-stone2"
        >
          {c.hero.subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.75, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-consult"
            className="inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-8 py-4 transition-all duration-500 hover:bg-bronze-light"
          >
            {c.hero.ctaPrimary} <ArrowRight size={14} />
          </Link>
          <Link
            to="/projects"
            data-testid="hero-cta-work"
            className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bone/80 hover:text-bronze transition-colors duration-500 py-4"
          >
            {c.hero.ctaSecondary} <ArrowUpRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Journey() {
  const c = useContent();
  const j = c.journey;
  return (
    <section data-testid="journey-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
      <FadeUp className="text-center mb-14">
        <Eyebrow className="mb-5">{j.eyebrow}</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone">{j.title}</h2>
      </FadeUp>
      <FadeUp delay={0.1}>
        <div className="flex flex-wrap items-center justify-center gap-y-6">
          {j.steps.map((s, i) => (
            <span key={s} className="flex items-center">
              <span className="font-serif text-xl sm:text-2xl font-light text-bone border border-white/10 px-6 py-4 transition-colors duration-500 hover:border-bronze/50 hover:text-bronze">
                {s}
              </span>
              {i < j.steps.length - 1 && <ArrowRight size={16} className="text-bronze mx-3 shrink-0" />}
            </span>
          ))}
        </div>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="mt-12 text-center font-serif italic text-xl sm:text-2xl font-light text-stone2 max-w-2xl mx-auto leading-snug">
          {j.message}
        </p>
      </FadeUp>
    </section>
  );
}

function Intro() {
  const c = useContent();
  return (
    <section data-testid="intro-section" className="border-y border-white/[0.07] bg-ink/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow className="mb-5">{c.intro.eyebrow}</Eyebrow>
              <p className="font-mono text-xs text-muted2 tracking-wider">{c.intro.tag}</p>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.1}>
              <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-snug text-bone">
                {c.intro.statement}
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link to="/about" data-testid="intro-about-link" className="mt-10 link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bronze py-2">
                Our Story <ArrowUpRight size={14} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const c = useContent();
  const MANIFESTO = c.manifesto;
  return (
    <section data-testid="manifesto-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
      <FadeUp className="mb-14">
        <Eyebrow className="mb-4">Our Values</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone">Built on three promises</h2>
      </FadeUp>
      <div>
        {MANIFESTO.map((chapter, i) => (
          <FadeUp key={chapter.num} delay={0.08 * i} className="group border-b border-white/[0.07] py-10 first:pt-0">
            <div className="flex flex-col sm:flex-row items-start gap-8 sm:gap-12">
              {chapter.image && (
                <div className="w-full sm:w-56 shrink-0 overflow-hidden">
                  <img src={chapter.image} alt={chapter.title} className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105" />
                </div>
              )}
              <div className="flex items-start gap-6 sm:gap-8">
                <span className="font-mono text-xs text-bronze pt-2">{chapter.num}</span>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-normal text-bone transition-colors duration-500 group-hover:text-bronze">
                    {chapter.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base font-light leading-relaxed text-stone2 max-w-2xl">{chapter.text}</p>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function FeaturedWork() {
  const c = useContent();
  const featured = c.projects.slice(0, 3);
  return (
    <section data-testid="featured-work-section" className="border-y border-white/[0.07] bg-ink/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-40">
        <div className="flex items-end justify-between mb-14">
          <FadeUp>
            <Eyebrow className="mb-5">Featured Work</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone">Flagship projects</h2>
          </FadeUp>
          <FadeUp delay={0.15} className="hidden sm:block">
            <Link to="/projects" data-testid="featured-view-all-link" className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bone/70 hover:text-bronze transition-colors duration-500 pb-1">
              View All <ArrowUpRight size={14} />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {featured.map((p, i) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`featured-project-${p.id}`} className="group block">
              <FadeUp delay={0.08 * i}>
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-2">{p.category} · {p.location}</p>
                      <h3 className="font-serif text-xl font-light text-bone">{p.title}</h3>
                    </div>
                    <span className="w-10 h-10 shrink-0 border border-bone/30 flex items-center justify-center text-bone transition-all duration-500 group-hover:bg-bronze group-hover:border-bronze group-hover:text-obsidian">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </div>
              </FadeUp>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const c = useContent();
  return (
    <section data-testid="why-us-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
      <FadeUp className="mb-14 text-center">
        <Eyebrow className="mb-5">Why Sai Leela Decor</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone">Why Choose Sai Leela Decor?</h2>
      </FadeUp>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
        {c.whyUs.map((w, i) => (
          <FadeUp key={w.num} delay={0.05 * i} className="bg-obsidian p-7 transition-colors duration-500 hover:bg-bronze/[0.05]">
            <p className="font-mono text-xs text-bronze mb-5">{w.num}</p>
            <h3 className="font-serif text-lg sm:text-xl font-light text-bone leading-snug">{w.title}</h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-stone2">{w.text}</p>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function ServicesPreview() {
  const c = useContent();
  return (
    <section data-testid="services-preview-section" className="border-y border-white/[0.07] bg-ink/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <FadeUp className="mb-12">
          <Eyebrow className="mb-5">What We Do</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone">Construction to interiors — one accountable team</h2>
        </FadeUp>
        <div>
          {c.services.map((s, i) => (
            <FadeUp key={s.num} delay={0.06 * i}>
              <Link
                to="/services"
                data-testid={`home-service-${s.num}`}
                className="group flex items-center justify-between gap-6 border-b border-white/[0.07] py-7 transition-colors duration-500 hover:bg-bronze/[0.04] px-2 sm:px-4"
              >
                <div className="flex items-baseline gap-6 sm:gap-10">
                  <span className="font-mono text-xs text-bronze">{s.num}</span>
                  <h3 className="font-serif text-xl sm:text-2xl font-light text-bone transition-colors duration-500 group-hover:text-bronze">{s.title}</h3>
                </div>
                <ArrowUpRight size={18} className="text-muted2 transition-all duration-500 group-hover:text-bronze group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const c = useContent();
  const STATS = c.stats;
  return (
    <section data-testid="stats-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
        {STATS.map((s, i) => (
          <FadeUp key={s.label} delay={0.08 * i} className="text-center lg:text-left">
            <p className="font-serif text-4xl sm:text-5xl font-light text-bronze">{s.value}</p>
            <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-muted2">{s.label}</p>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const c = useContent();
  const TESTIMONIALS = c.testimonials;
  const [index, setIndex] = useState(0);

  return (
    <section data-testid="testimonials-section" className="border-y border-white/[0.07] bg-ink/40">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
        <FadeUp><Eyebrow className="mb-10">Client Words</Eyebrow></FadeUp>
        {TESTIMONIALS.length === 0 ? (
          <FadeUp delay={0.1}>
            <p className="font-serif italic text-2xl sm:text-3xl font-light leading-snug text-bone max-w-2xl mx-auto">
              Genuine client stories are being collected — real words from real handovers will live here soon.
            </p>
            <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-muted2">Video testimonials · Coming soon</p>
          </FadeUp>
        ) : (
          <>
            <div className="min-h-[180px] flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.blockquote
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6, ease: EASE }}
                >
                  <p className="font-serif italic text-2xl sm:text-3xl font-light leading-snug text-bone">&ldquo;{TESTIMONIALS[index].quote}&rdquo;</p>
                  <footer className="mt-8">
                    <p className="text-xs uppercase tracking-[0.3em] text-bronze">{TESTIMONIALS[index].author}</p>
                    <p className="mt-2 text-xs text-muted2 tracking-wider">{TESTIMONIALS[index].project}</p>
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
            </div>
            <div className="mt-12 flex items-center justify-center gap-4">
              <button data-testid="testimonial-prev-btn" onClick={() => setIndex((index - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone/70 transition-all duration-500 hover:border-bronze hover:text-bronze" aria-label="Previous testimonial">
                <ArrowLeft size={16} />
              </button>
              <span className="font-mono text-xs text-muted2">{String(index + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}</span>
              <button data-testid="testimonial-next-btn" onClick={() => setIndex((index + 1) % TESTIMONIALS.length)} className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone/70 transition-all duration-500 hover:border-bronze hover:text-bronze" aria-label="Next testimonial">
                <ArrowRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function VideoSection() {
  const c = useContent();
  const v = c.videoSection;
  return (
    <section data-testid="video-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
      <FadeUp>
        <div className="relative border border-white/[0.07] bg-ink/40 overflow-hidden">
          {v.url ? (
            <div className="aspect-video">
              <iframe src={v.url} title={v.title} className="w-full h-full border-0" loading="lazy" allowFullScreen />
            </div>
          ) : (
            <div className="aspect-[16/7] flex flex-col items-center justify-center text-center px-6 relative">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(197,160,89,0.1),transparent_60%)]" />
              <span className="relative w-16 h-16 border border-bronze/50 flex items-center justify-center text-bronze mb-8">
                <Play size={20} />
              </span>
              <Eyebrow className="mb-4 relative">{v.eyebrow}</Eyebrow>
              <p className="relative font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-bone max-w-2xl leading-snug">{v.title}</p>
              <p className="relative mt-5 text-xs tracking-wider text-muted2 max-w-md">{v.note}</p>
            </div>
          )}
        </div>
      </FadeUp>
    </section>
  );
}

function CostSection() {
  const c = useContent();
  const cs = c.costSection;
  return (
    <section data-testid="cost-section" className="border-y border-white/[0.07] bg-ink/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <FadeUp>
            <Eyebrow className="mb-5">{cs.eyebrow}</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-bone max-w-xl">{cs.title}</h2>
            <p className="mt-6 text-base font-light leading-relaxed text-stone2 max-w-lg">{cs.intro}</p>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-8 flex flex-wrap gap-2">
            {cs.factors.map((f) => (
              <span key={f} className="text-[10px] uppercase tracking-[0.2em] text-bone/70 border border-white/10 px-4 py-2">{f}</span>
            ))}
          </FadeUp>
        </div>
        <div className="lg:col-span-5 flex items-center">
          <FadeUp delay={0.15} className="w-full border border-bronze/25 bg-bronze/[0.04] p-10 text-center">
            <p className="font-serif text-2xl sm:text-3xl font-light text-bone">{cs.sub}</p>
            <Link to="/contact" data-testid="cost-estimate-btn" className="mt-8 inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light">
              {cs.cta} <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const c = useContent();
  return (
    <section data-testid="cta-banner" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(197,160,89,0.12),transparent_65%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-40 text-center relative">
        <FadeUp>
          <Eyebrow className="mb-6">{c.cta.eyebrow}</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-bone leading-[1.08]">
            {c.cta.titleA} <span className="italic text-bronze">{c.cta.titleB}</span>
          </h2>
          <Link
            to="/contact"
            data-testid="cta-banner-btn"
            className="mt-12 inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light"
          >
            {c.cta.button} <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  usePageTitle("Sai Leela Decor — Turnkey Bungalow Construction & Interiors | Thane · Mumbai · Maharashtra");
  return (
    <PageShell testid="home-page">
      <Hero />
      <Marquee />
      <Journey />
      <Intro />
      <Manifesto />
      <FeaturedWork />
      <WhyUs />
      <ServicesPreview />
      <Stats />
      <Testimonials />
      <VideoSection />
      <CostSection />
      <CtaBanner />
    </PageShell>
  );
}
